/**
 * Privacy Policy Screen - MOBILE ONLY
 * 
 * Privacy policy.
 * Stacked flow - back button, no bottom tabs.
 */

import React, { useCallback } from 'react';
import { View, Text, ScrollView, StyleSheet, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { color, text, space, touch, icon, layout } from '../../../shared/tokens/mobile';
import { ScreenContainer } from '../../../components/v2/mobile';

export default function PrivacyScreen() {
  const router = useRouter();

  const handleBack = useCallback(() => {
    router.back();
  }, [router]);

  return (
    <ScreenContainer>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* HEADER */}
        <View style={styles.header}>
          <Pressable onPress={handleBack} style={styles.backButton}>
            <Ionicons name="chevron-back" size={icon.lg} color={color.textPrimary} />
          </Pressable>
          <Text style={styles.title}>Privacy Policy</Text>
          <View style={styles.spacer} />
        </View>

        {/* CONTENT */}
        <View style={styles.content}>
          <Text style={styles.lastUpdated}>Last updated: December 2024</Text>

          <Text style={styles.sectionTitle}>Information We Collect</Text>
          <Text style={styles.paragraph}>
            We collect information you provide directly, including account information (email, name), property data you enter, and communications within the app.
          </Text>

          <Text style={styles.sectionTitle}>How We Use Your Information</Text>
          <Text style={styles.paragraph}>
            We use your information to provide and improve our services, communicate with you, and ensure the security of your account.
          </Text>

          <Text style={styles.sectionTitle}>Data Storage and Security</Text>
          <Text style={styles.paragraph}>
            Your data is stored securely using industry-standard encryption. We implement appropriate technical and organizational measures to protect your personal information.
          </Text>

          <Text style={styles.sectionTitle}>Information Sharing</Text>
          <Text style={styles.paragraph}>
            We do not sell your personal information. We may share data with partners you explicitly invite to your portfolios, and with service providers who assist in operating our service.
          </Text>

          <Text style={styles.sectionTitle}>Your Rights</Text>
          <Text style={styles.paragraph}>
            You have the right to access, correct, or delete your personal information. You can export your data or request account deletion by contacting support.
          </Text>

          <Text style={styles.sectionTitle}>Cookies and Tracking</Text>
          <Text style={styles.paragraph}>
            We use essential cookies for authentication and session management. We do not use third-party advertising trackers.
          </Text>

          <Text style={styles.sectionTitle}>Data Retention</Text>
          <Text style={styles.paragraph}>
            We retain your data for as long as your account is active. Upon account deletion, we remove your personal data within 30 days, except where retention is required by law.
          </Text>

          <Text style={styles.sectionTitle}>Changes to This Policy</Text>
          <Text style={styles.paragraph}>
            We may update this policy periodically. We will notify you of significant changes via email or in-app notification.
          </Text>

          <Text style={styles.sectionTitle}>Contact Us</Text>
          <Text style={styles.paragraph}>
            For privacy-related inquiries, contact us at privacy@farmerjohn.app.
          </Text>
        </View>
      </ScrollView>
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: layout.scrollBottomPadding,
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: space.sm,
    paddingVertical: space.sm,
  },
  backButton: {
    width: touch.minimum,
    height: touch.minimum,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    flex: 1,
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
    textAlign: 'center',
  },
  spacer: {
    width: touch.minimum,
  },

  // Content
  content: {
    paddingHorizontal: space.md,
    paddingTop: space.md,
  },
  lastUpdated: {
    fontSize: text.caption.fontSize,
    fontWeight: text.caption.fontWeight,
    color: color.textMuted,
    marginBottom: space.lg,
  },
  sectionTitle: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
    marginTop: space.lg,
    marginBottom: space.sm,
  },
  paragraph: {
    fontSize: text.body.fontSize,
    fontWeight: text.body.fontWeight,
    lineHeight: text.body.lineHeight + 4,
    color: color.textSecondary,
  },
});

